import { useState, type FormEvent, type Ref } from 'react';
import { DataTable, GS_COMPONENTS, type Column } from '@gs/ui';
import { useTranslation } from '../../../i18n';
import {
  useUpsertYearConfig,
  type AdminYearConfig,
} from '../../../hooks/useAdminRemunerationConfig';
import { SectionHeader } from '../SectionHeader';
import { FieldLabel } from './fields';
import { INPUT_MEDIUM } from './scale';

const CAD2 = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' });

export function YearCostSection({
  year,
  yearConfigs,
  autoFocus,
  inputRef,
  onError,
  onClear,
  onSuccess,
  onEditYear,
}: {
  year: string;
  yearConfigs: AdminYearConfig[];
  autoFocus: boolean;
  inputRef: Ref<HTMLInputElement>;
  onError: (err: unknown) => void;
  onClear: () => void;
  onSuccess: () => void;
  onEditYear: (year: number) => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertYearConfig();
  const yearNum = Number(year);
  const costFor = (y: number): string => {
    const c = yearConfigs.find((row) => row.year === y)?.vehicleCostBiweekly;
    return c == null ? '' : String(c);
  };
  const [cost, setCost] = useState(() => costFor(yearNum));

  const rows = yearConfigs.filter((y) => y.vehicleCostBiweekly != null);
  const columns: Column<AdminYearConfig>[] = [
    { key: 'year', header: t('gest:admin.remuneration.config.costs.year'), render: (r) => String(r.year) },
    {
      key: 'cost',
      header: t('gest:admin.remuneration.config.costs.biweekly'),
      render: (r) => (r.vehicleCostBiweekly == null ? '—' : CAD2.format(r.vehicleCostBiweekly)),
    },
    {
      key: 'annual',
      header: t('gest:admin.remuneration.config.costs.annual'),
      render: (r) => (r.vehicleCostBiweekly == null ? '—' : CAD2.format(r.vehicleCostBiweekly * 26)),
    },
    {
      key: 'edit',
      header: t('gest:admin.remuneration.config.editYear'),
      render: (r) => (
        <button
          type="button"
          className={`${GS_COMPONENTS.btnGhost} touch-manipulation`}
          onClick={() => onEditYear(r.year)}
        >
          {t('gest:admin.remuneration.config.editYear')}
        </button>
      ),
    },
  ];

  function submit(e: FormEvent) {
    e.preventDefault();
    const y = Number(year);
    const c = Number(cost);
    if (!Number.isInteger(y) || y < 2000 || y > 2100 || !Number.isFinite(c) || c < 0) return;
    onClear();
    upsert.mutate(
      { year: y, vehicleCostBiweekly: c },
      { onError, onSuccess },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.costs.title')}
        description={t('gest:admin.remuneration.config.costs.description')}
      />
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="ad2-cost-vehicle">{t('gest:admin.remuneration.config.costs.biweekly')}</FieldLabel>
          <input
            id="ad2-cost-vehicle"
            ref={inputRef}
            autoFocus={autoFocus}
            type="number"
            min={0}
            max={100000}
            step="0.01"
            required
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className={INPUT_MEDIUM}
          />
        </div>
        <button type="submit" disabled={upsert.isPending} className={GS_COMPONENTS.btnAction}>
          {t('gest:admin.remuneration.config.costs.save')}
        </button>
      </form>
      {rows.length === 0 ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:admin.remuneration.config.costs.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable columns={columns} data={rows} rowKey={(r) => String(r.year)} />
        </div>
      )}
    </div>
  );
}
